/**
 * Two-input numeric range. Either bound may be empty. Used for diameter
 * filtering on the wheels page (and any other range filter we add later).
 */

import { useState } from 'react';
import {
  Popover, Button, BlockStack, InlineStack, TextField, Box,
} from '@shopify/polaris';

export interface RangeFilterProps {
  label: string;
  /** Optional unit suffix shown after each input (e.g. `"`). */
  unit?: string;
  min?: number;
  max?: number;
  onChange: (next: { min?: number; max?: number }) => void;
}

export function RangeFilter({ label, unit, min, max, onChange }: RangeFilterProps) {
  const [open, setOpen] = useState(false);
  const [minLocal, setMinLocal] = useState(min == null ? '' : String(min));
  const [maxLocal, setMaxLocal] = useState(max == null ? '' : String(max));

  // Sync local state when URL state changes externally (e.g. user
  // clicks a chip's X button to clear the range).
  if (min !== undefined && minLocal === '' && document.activeElement?.tagName !== 'INPUT') {
    setMinLocal(String(min));
  }
  if (max !== undefined && maxLocal === '' && document.activeElement?.tagName !== 'INPUT') {
    setMaxLocal(String(max));
  }

  const apply = () => {
    const minN = minLocal === '' ? undefined : Number(minLocal);
    const maxN = maxLocal === '' ? undefined : Number(maxLocal);
    onChange({
      min: Number.isFinite(minN as number) ? (minN as number) : undefined,
      max: Number.isFinite(maxN as number) ? (maxN as number) : undefined,
    });
    setOpen(false);
  };

  const clear = () => {
    setMinLocal('');
    setMaxLocal('');
    onChange({ min: undefined, max: undefined });
    setOpen(false);
  };

  const active = min !== undefined || max !== undefined;
  const activatorLabel = !active
    ? label
    : `${label}: ${min ?? ''}${unit ?? ''}–${max ?? ''}${unit ?? ''}`;

  return (
    <Popover
      active={open}
      onClose={() => setOpen(false)}
      activator={
        <Button disclosure pressed={active} onClick={() => setOpen((v) => !v)}>
          {activatorLabel}
        </Button>
      }
    >
      <Box padding="300" minWidth="240px">
        <BlockStack gap="300">
          <InlineStack gap="200">
            <TextField
              label="Min"
              type="number"
              value={minLocal}
              onChange={setMinLocal}
              autoComplete="off"
              suffix={unit}
            />
            <TextField
              label="Max"
              type="number"
              value={maxLocal}
              onChange={setMaxLocal}
              autoComplete="off"
              suffix={unit}
            />
          </InlineStack>
          <InlineStack gap="200" align="space-between">
            <Button variant="plain" onClick={clear}>Clear</Button>
            <Button variant="primary" onClick={apply}>Apply</Button>
          </InlineStack>
        </BlockStack>
      </Box>
    </Popover>
  );
}
